/**
 * Detects whether an on-screen keyboard is open, in both viewport modes.
 *
 * Default mode (Chrome's default, `interactive-widget=resizes-visual`): the
 * keyboard shrinks only the visual viewport, so
 * `window.innerHeight - visualViewport.height` jumps by the keyboard height.
 *
 * `interactive-widget=resizes-content`: the keyboard shrinks the layout
 * viewport too, so `innerHeight` falls together with the visual viewport and
 * that difference stays about 0. The second test therefore compares against
 * the largest height seen at the current width (the remembered pre-keyboard
 * height) and requires a focused editable element, so a URL-bar change or a
 * split-screen resize alone is not mistaken for a keyboard.
 */
import { getActiveKeyboardMode } from '@/lib/keyboardMode';

/** The keyboard counts as open once it covers more than this many px. */
export const KEYBOARD_THRESHOLD_PX = 150;
/**
 * Once open, it counts as closed only when it covers fewer than this many px.
 * The gap to KEYBOARD_THRESHOLD_PX is the hysteresis band: a height that
 * wobbles around one threshold (the keyboard animation, a URL bar settling)
 * cannot flip the answer back and forth.
 */
const KEYBOARD_CLOSE_THRESHOLD_PX = 100;
/** A change must hold this long before the UI follows it (see useKeyboardOpen). */
export const KEYBOARD_SETTLE_MS = 150;

let baselineWidth = 0;
let baselineHeight = 0;

/** Test hook: forget the remembered pre-keyboard height. */
export function resetKeyboardBaseline(): void {
  baselineWidth = 0;
  baselineHeight = 0;
}

/** True when focus is in something that raises a soft keyboard. */
export function isEditableElement(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select' || tag === 'input') return true;
  const attr = el.getAttribute('contenteditable');
  if (attr === '' || attr === 'true' || attr === 'plaintext-only') return true;
  // Ternary, not &&: jsdom leaves isContentEditable undefined.
  return el instanceof HTMLElement && el.isContentEditable ? true : false;
}

/**
 * @param wasOpen the previous answer. While open, the keyboard must shrink
 *   below KEYBOARD_CLOSE_THRESHOLD_PX to read as closed (hysteresis).
 */
export function isKeyboardOpen(wasOpen = false): boolean {
  if (typeof window === 'undefined') return false;
  const viewport = window.visualViewport;
  if (!viewport) return false;
  const threshold = wasOpen ? KEYBOARD_CLOSE_THRESHOLD_PX : KEYBOARD_THRESHOLD_PX;

  // Default mode: only the visual viewport shrinks.
  if (window.innerHeight - viewport.height > threshold) return true;

  // Only when the page asked for resizes-content. Everywhere else (the
  // default mode) the test above is the whole answer, so a plain window
  // resize with a focused field can never read as a keyboard.
  if (getActiveKeyboardMode() !== 'content') return false;

  // A new width means rotation or a fold: the old height is meaningless.
  if (window.innerWidth !== baselineWidth) {
    baselineWidth = window.innerWidth;
    baselineHeight = 0;
  }
  baselineHeight = Math.max(baselineHeight, window.innerHeight, viewport.height);

  // resizes-content mode: both heights shrank, so use the remembered height.
  return isEditableElement(document.activeElement) && baselineHeight - viewport.height > threshold;
}
